import { captureException } from '@sentry/browser';
import styled from '@emotion/styled';
import { HeadingText50 as Title, BodyTextMedium } from '@modernhealth/vital-components';
import { mediaQuery } from '@modernhealth/vital-foundation';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';

import { WellbeingReportTakeAssessment } from 'src/modules/assessments/components/WellbeingReportTakeAssessment';
import { WellbeingReportChart } from 'src/modules/assessments/components/WellbeingReportChart';
import { WellbeingReportMonitoringMessage } from 'src/modules/assessments/components/WellbeingReportMonitoringMessage';
import { TopicSelectionGroupSelectionType } from 'src/generated/webapp_gql';
import { TopicSelection } from 'src/modules/assessments/components/WellbeingReportTopicSelection';
import { YourNextStep } from 'src/modules/assessments/components/WellbeingReportYourNextStep';
import { useWellBeingReportQuery } from 'src/modules/assessments/documents/getWellbeingReportQuery.generated';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { topicSelectionPaths } from 'src/shared/hooks/useOnboardingGuard';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
    flex: 1,
    maxWidth: 740,
    width: '100%',
    margin: '0 auto',
    marginTop: 64,
    marginBottom: 100,
    [mediaQuery.maxWidth('sm')]: {
        padding: '0.5rem',
        marginTop: 8,
    },
});

interface CustomizedState {
    prevPathName?: string;
    loadingText?: string;
}

export const WellbeingReportPage: React.FC<
    RouteComponentProps<{}, {}, CustomizedState | undefined>
> = ({ history, location }) => {
    const { t } = useTranslation();
    const { data, loading, error } = useWellBeingReportQuery({
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (error) {
            captureException(
                new Error('An unexpected wellBeingReport chart query error was found'),
            );
        }
    }, [error]);

    const response = data?.wellBeingReport;
    const wellbeingReport = response?.data;
    const metadata = response?.metadata;

    const loadingText = location.state?.loadingText
        ? location.state.loadingText
        : t('assessments:wellbeing_report.loading_text.loading_topics');
    const pathname = location.pathname;

    const handleUpdateTopics = () => {
        history.push(
            `/onboarding-categories/${
                topicSelectionPaths[TopicSelectionGroupSelectionType.WellbeingReport]
            }`,
            {
                prevPathName: pathname,
                loadingText,
            },
        );
    };

    const hasData = wellbeingReport && wellbeingReport.length > 0;

    if (loading) {
        return <CenteredSpinner />;
    }

    return (
        <Wrapper>
            <Title marginBottom={hasData ? 8 : 32}>{t('assessments:wellbeing_report.title')}</Title>
            {!hasData && <WellbeingReportTakeAssessment />}
            {hasData && (
                <>
                    <BodyTextMedium>{t('assessments:wellbeing_report.subtitle')}</BodyTextMedium>
                    <WellbeingReportChart
                        loading={loading}
                        wellbeingReport={wellbeingReport}
                        metadata={metadata}
                    />
                </>
            )}
            <WellbeingReportMonitoringMessage />
            <TopicSelection loadingText={loadingText} handleUpdateTopics={handleUpdateTopics} />
            {hasData && <YourNextStep />}
        </Wrapper>
    );
};
